/**
 * @fileDescription Product validation schema using Yup validation
 * @author Pankaj Kulshreshtha | Cheers Interactive
 * @date 03/Aug/2020 *
 */
import * as Yup from "yup";
import * as REGEX from "../../../config/regexValidation";
import { PRODUCT_LOGO_IMG_SIZE } from "../../../config";
import { stripHtml } from "utils";

// Product Overview Validation
export const productValidationSchema = {
  productSourceURL: Yup.string().url("Please enter valid product source URL"),
  productName: Yup.string()
    .required("Please enter product name")
    .min(2, "The minimum character limit is 2.")
    .max(150, "You have exceeded the maximum character limit of 150.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  yearLaunched: Yup.string()
    .nullable()
    .notRequired()
    .matches(REGEX.YEAR, "Please enter a valid 4-digit year")
    .test("not-in-future", "Year cannot be in the future", function (value) {
      if (!value) return true;
      return parseInt(value, 10) <= new Date().getFullYear();
    }),
  licencingAvailablity: Yup.string()
    .min(2, "The minimum character limit is 2")
    .max(150, "You have exceeded the maximum character limit of 150.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  domainClassification: Yup.string()
    .min(2, "The minimum character limit is 2")
    .max(150, "You have exceeded the maximum character limit of 150.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  productDescription: Yup.string()
    .transform((value) => stripHtml(value))
    .trim("Please enter the product description.")
    .required("Please enter the product description.")
    .min(25, "The minimum character limit is 25.")
    .max(5000, "You have exceeded the maximum character limit of 5000."),
  productWhatsUnique: Yup.string()
    .transform((value) => stripHtml(value))
    .min(25, "The minimum character limit is 25.")
    .max(5000, "You have exceeded the maximum character limit of 5000."),
  productProfileImage: Yup.mixed().required("Please upload image"),
};
// Product Value Proposition Validation
export const valuePropValidationSchema = {
  timeReduction: Yup.string().required("Please enter time reduction"),
};
// Product Document Brochure Validation
export const addDocumentValidationSchema = {
  documentTitle: Yup.string()
    .required("Please enter document name")
    .min(1, "The minimum character limit is 1")
    .max(200, "You have exceeded the maximum character limit of 200.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  documentDescription: Yup.string()
    .transform((value) => stripHtml(value))
    .trim("Please enter document description")
    .required("Please enter document description")
    .min(25, "The minimum character limit is 25.")
    .max(1000, "You have exceeded the maximum character limit of 1000."),
  documentFile: Yup.mixed().required("Please upload document"),
};
// Product Edit Document Brochure Validation
export const editDocumentValidationSchema = {
  documentTitle: Yup.string()
    .required("Please enter document name")
    .min(1, "The minimum character limit is 1")
    .max(200, "You have exceeded the maximum character limit of 200.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  documentDescription: Yup.string()
    .transform((value) => stripHtml(value))
    .trim("Please enter document description")
    .required("Please enter document description")
    .min(25, "The minimum character limit is 25.")
    .max(1000, "You have exceeded the maximum character limit of 1000."),
};
// Product Video Validation
export const videoValidationSchema = {
  videoTitle: Yup.string()
    .required("Please enter video title")
    .min(2, "The minimum character limit is 2")
    .max(150, "You have exceeded the maximum character limit of 150.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  videoDescription: Yup.string(),
  videoSource: Yup.string().typeError("Please select video source").required("Please select video source"),
  videoId: Yup.string()
    .required("Please enter video id")
    .min(2, "The minimum character limit is 2")
    .max(150, "You have exceeded the maximum character limit of 150.")
    .matches(REGEX.COMPANY, "Invalid characters"),
  videoThumbnail: Yup.mixed().required("Please upload video thumbnail"),
  videoDurationInMinute: Yup.number()
    .typeError("Please enter a valid number")
    .required("Please enter video duration")
    .integer("Only whole numbers are allowed")
    .min(1, "Video duration must be at least 1 minute")
    .max(9999, "Video duration cannot exceed 9999 minutes"),
};
// Product document Brochure Validation
export const imageValidationSchema = {
  imageFile: Yup.mixed()
    .required("Please upload image")
    .test("imageFile", "File size is more than 10 MB", (value) => {
      console.log("value", value);
      if (value && typeof value === "object" && "size" in value) {
        return value.size <= PRODUCT_LOGO_IMG_SIZE;
      }
      return true;
    }),
};

export const techSpecificationSchema = {
  specificationName: Yup.string().typeError("Please enter parameter name").min(2).required("Please enter parameter name"),
  specificationUnit: Yup.string().min(2).required("Please select unit of measurement"),
  minValue: Yup.number().required("Please enter parameter min value"),
  maxValue: Yup.number().required("Please enter parameter max value"),
  singleValue: Yup.string().required("Please enter parameter value"),
};

// Product Collaboration Validation
export const collaborationValidationSchema = {
  collaborationCompany: Yup.array().required("Please select atleast one organization"),
  collaborationType: Yup.string().required("Please select nature of relationship"),
  collaborationDate: Yup.string().matches(REGEX.YEAR, "Please enter valid 4 digit year").required("Please enter year of engagement"),
  collobarationAttachment: Yup.mixed(),
  collaborationDescription: Yup.string(),
};
